import { type FC, useEffect, useState } from 'react';
import { first, isEmpty, isNil } from 'lodash';
// Import Types
import type { PushNotificationSettingsCascadeConfig } from 'types';
// Import Components
import CascadeForm from '../DynamicSegment/DynamicSegmentForms/CascadeForm';
// Import Constants
import { CascadeUITypes } from 'constants/common';
// Import Styles
import styles from './pushNotificationCascadeForm.module.scss';

type ValueData = { Id: string | number }[][];

export interface PushNotificationCascadeFormProps {
    cascade: Nullable<PushNotificationSettingsCascadeConfig>;
    value: AnyObject;
    isLoading: boolean;
    onChange: (value: AnyObject) => void;
    validationChange: (isValid: boolean) => void;
}

const PushNotificationCascadeForm: FC<PushNotificationCascadeFormProps> = (props) => {
    const { cascade, value, isLoading, onChange, validationChange } = props;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [defaultValue, setDefaultValue] = useState<Nullable<{ data: ValueData; type: any }>>(null);

    useEffect(() => {
        if (!isLoading && !isEmpty(cascade?.Cascade)) {
            let typeValue;
            const data: ValueData = (cascade?.Cascade as unknown as AnyObject[]).map((item, index) => {
                if (index === 0) {
                    typeValue = value[item.Property];
                }
                if (!isNil(value[item.Property]) && (!isNil(item.MenuItemId) || item.IsMultiselect)) {
                    return `${value[item.Property]}`.split(',').map((itemValue) => {
                        return { Id: itemValue };
                    });
                }

                return [{ Id: value[item.Property] }];
            });
            setDefaultValue({
                data: data,
                type: typeValue ?? null,
            });
        }
        if (!isLoading && isEmpty(cascade?.Cascade)) {
            validationChange(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, cascade]);

    if (isLoading || isEmpty(cascade?.Cascade) || isNil(defaultValue)) {
        return <></>;
    }

    const getPropertyValue = (item: AnyObject, value: { [key: string]: ValueData[number] }, index: number) => {
        if (!isNil(item.MenuItemId)) {
            return value[index].join(',');
        }

        if ([CascadeUITypes.DropDown, CascadeUITypes.Tree].includes(item.UIType)) {
            if (item.IsMultiselect) {
                return value[index].map((el) => el.Id).join(',');
            } else {
                return first(value[index])?.Id;
            }
        }

        if ([CascadeUITypes.Text, CascadeUITypes.Number].includes(item.UIType)) {
            return first(value[index]);
        }
    };

    return (
        <div className={styles.pushNotificationCascadeForm}>
            <CascadeForm
                key={JSON.stringify(cascade)}
                setFormData={(e) => {
                    const val = e?.Data?.CascadeFiltersList ?? [];
                    const value = (cascade?.Cascade as unknown as AnyObject[]).reduce((acc, item, index) => {
                        acc[item.Property] = isNil(val[index]) ? null : (getPropertyValue(item, val, index) ?? null);
                        return acc;
                    }, {});

                    onChange({ ...value, type: 0 });
                }}
                data={{
                    id: defaultValue?.type ?? null,
                    comparison: { source: JSON.stringify(cascade) },
                    parent: { hashKey: 'push' },
                }}
                filterList={{
                    push: {
                        [defaultValue?.type]: { Value: { Data: { CascadeFiltersList: defaultValue.data } } },
                    },
                }}
                withFormApplyAndResetButtonContainer={false}
                validateOnChange={true}
                getValidationUpdate={validationChange}
            />
        </div>
    );
};

export default PushNotificationCascadeForm;
